import React, { useState, useEffect } from "react";
import { Select, Tooltip } from "antd";
import { SvgIcons } from "Icon/SvgIcons";
import { getRecruiterQuestionSets } from "api/screeningquestions.api";
import { debounce } from 'lodash';

interface SelectOption {
    value: string;
    label: string;
}

// Define the interface for a question
interface Question {
    id: number;
    created: string;
    modified: string;
    text: string;
    order: number;
    question_set: number;
}

// Define the interface for a question set
interface QuestionSet {
    id: number;
    created: string;
    modified: string;
    name: string;
    recruiter: number;
    questions: Question[];
}

interface QuestionSetResponse {
    count: number;
    results: QuestionSet[];
}

interface QuestionSetTemplateSelectorProps {
    onSelect: (selectedValue: number, questionList: any, questionSetName: string) => void;
    isLarge?: boolean;
    isLabel?: boolean;
    refresh?: boolean;
    questionId?: number;
    setQuestionId?: any;
    setRefresh?: React.Dispatch<React.SetStateAction<boolean>>;
}


const QuestionSetTemplateSelector: React.FC<QuestionSetTemplateSelectorProps> = ({ onSelect, isLarge, isLabel = true, refresh, setRefresh, questionId, setQuestionId }) => {
    const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
    const [page, setPage] = useState<number>(1);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [options, setOptions] = useState<SelectOption[]>([]);
    const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined);

    const [questionText, setQuestionText] = useState<any[]>([]);
    const [refreshQuestionSets, setRefreshQuestionSets] = useState<boolean>(false);

    const qSet = {
        "id": 5,
        "created": "2024-10-23T11:08:39.243509Z",
        "modified": "2024-10-23T11:08:39.243534Z",
        "name": "new Qset1",
        "recruiter": 4,
        "questions": [
            {
                "id": 13,
                "created": "2024-10-23T11:08:39.271548Z",
                "modified": "2024-10-28T19:18:37.100832Z",
                "text": "how do you do this?",
                "order": 0,
                "question_set": 5
            },
            {
                "id": 14,
                "created": "2024-10-28T19:18:53.701213Z",
                "modified": "2024-10-28T19:18:53.701241Z",
                "text": "what is your expertise?",
                "order": 1,
                "question_set": 5
            },
            {
                "id": 15,
                "created": "2024-10-23T11:08:39.271548Z",
                "modified": "2024-10-28T19:18:37.100832Z",
                "text": "how do you do this?",
                "order": 0,
                "question_set": 5
            },
            {
                "id": 16,
                "created": "2024-10-28T19:18:53.701213Z",
                "modified": "2024-10-28T19:18:53.701241Z",
                "text": "what is your expertise?",
                "order": 1,
                "question_set": 5
            }
        ]
    };
    // const [options, setOptions] = useState([]);

    const fetchQuestionSets = async () => {
        try {
            const response: QuestionSetResponse = await getRecruiterQuestionSets(page);
            setQuestionSets(prev => [...prev, ...response.results]);
            if (page >= response.count / 10) {
                setHasMore(false);
            }
            return response;
        } catch (error) {
            console.error("Error fetching pipeline list:", error);
            return null;
        }
    };

    useEffect(() => {
        fetchQuestionSets();
    }, [page]);

    useEffect(() => {
        const handleRefresh = async () => {
            if (refresh) {
                setQuestionSets([]);
                setPage(1);
                setHasMore(true);
                const response = await fetchQuestionSets();
                // Set the first question set as selected if available
                if (response && response?.results?.length > 0 && setQuestionId) {
                    setQuestionId(response.results[0]?.id);
                }
                
                if (setRefresh) setRefresh(false);
            }
        };
        
        handleRefresh();
    }, [refresh]);

    useEffect(() => {
        // Transform the questionSets data into the options format for Select
        const options = questionSets.map((item) => ({
            value: item.id.toString(), // Use `id` for the value
            label: item.name // Use `name` for the label
        }));

        setOptions(options);
    }, [questionSets]);


    const handleChange = (value: any) => {
        console.log("Question Set: ", value);
        const intValue = parseInt(value, 10);
        const questionsList = questionSets.find(question => question.id === intValue);
        const selectedSet = questionSets.find(set => set.id === intValue);
        // const selectedSet = qSet;
        console.log("Selected Set: ", selectedSet);
        if (selectedSet) {
            // Sort questions based on 'order' and format them for display
            const orderedQuestions = selectedSet.questions
                .sort((a, b) => a.order - b.order) // Sort questions by their order
                .map(question => question); // Get the text of each question
            // .join('<br />');

            setQuestionText(orderedQuestions);
            onSelect(intValue, orderedQuestions, selectedSet.name); // Call the prop function with both values
        }
    };

    const handlePopupScroll = debounce(async (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - (scrollTop + clientHeight) < 50;

        if (isNearBottom && !loadingMore && hasMore) {
            setLoadingMore(true);
            setPage(page + 1);
            setLoadingMore(false);
        }
    }, 200);

    return (
        <div className="flex flex-col gap-1.5 w-full">
            {isLabel && <label htmlFor="questionSetTemplate" className="text-sm flex items-center gap-0.5 font-medium text-gray-700">
                Select template <span className="text-primary">*</span><Tooltip title="Tooltip text" style={{ justifyContent: "center" }}>{SvgIcons.questionSvg}</Tooltip>
            </label>}

            <Select
                id="questionSetTemplate"
                placeholder="Select question bank"
                options={options}
                value={questionId ? String(questionId) : undefined}
                style={{ width: "100%" }}
                onChange={handleChange}
                size={isLarge ? "large" : "middle"}
                dropdownStyle={{ maxHeight: 250, overflowY: "auto" }}
                onPopupScroll={handlePopupScroll} // Detect scroll inside dropdown
                loading={loadingMore} // Show loading indicator
                dropdownRender={(menu) => (
                    <div
                        style={{ maxHeight: 250, overflowY: 'auto' }}
                        onScroll={handlePopupScroll}
                    >
                        {menu}
                    </div>
                )}
            />
        </div>
    );
};

export default QuestionSetTemplateSelector;

